import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { NAV_ITEMS } from '../utils/constants';

const Navbar: React.FC = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => location.pathname === path;
  const isDropdownActive = (item: any) => {
    if (!item.children) return false;
    return item.children.some((child: any) => isActive(child.path)) || isActive(item.path);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleDropdown = (path: string) => {
    setOpenDropdown(openDropdown === path ? null : path);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navItemClass = (active: boolean) =>
    `group relative flex items-center px-4 lg:px-5 py-3.5 lg:py-4 text-sm lg:text-[15px] tracking-wide transition-colors duration-200 ${
      active
        ? 'text-white font-medium'
        : 'text-white/80 hover:text-white'
    }`;

  const underlineClass = (active: boolean) =>
    `absolute bottom-0 left-4 right-4 h-0.5 bg-white origin-left transition-transform duration-300 ease-out ${
      active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
    }`;

  return (
    <nav className="bg-primary-600 sticky top-0 z-50 shadow-soft">
      <div className="container">
        <div className="flex justify-between items-center min-h-[52px]">
          <ul className="hidden md:flex items-center">
            {NAV_ITEMS.map((item) => (
              <li key={item.path} className="relative">
                {item.children ? (
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => toggleDropdown(item.path)}
                      className={navItemClass(isDropdownActive(item))}
                      aria-expanded={openDropdown === item.path}
                    >
                      <span>{item.label}</span>
                      <i
                        className={`fas fa-chevron-down ml-2 text-[10px] transition-transform duration-200 ${
                          openDropdown === item.path ? 'rotate-180' : ''
                        }`}
                      ></i>
                      <span className={underlineClass(isDropdownActive(item))}></span>
                    </button>

                    {openDropdown === item.path && (
                      <div className="absolute top-full left-0 min-w-[11rem] bg-white border border-primary-100 shadow-lift overflow-hidden z-50 animate-slide-up">
                        <ul className="py-2">
                          {item.children.map((child) => (
                            <li key={child.path}>
                              <NavLink
                                to={child.path}
                                className={`block px-4 py-2.5 text-sm text-primary-700 hover:bg-primary-50 hover:text-primary-900 transition-colors duration-200 ${
                                  isActive(child.path) ? 'bg-primary-50 text-primary-900 font-medium' : ''
                                }`}
                                onClick={() => setOpenDropdown(null)}
                              >
                                {child.label}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <NavLink to={item.path} className={navItemClass(isActive(item.path))}>
                    <span>{item.label}</span>
                    <span className={underlineClass(isActive(item.path))}></span>
                  </NavLink>
                )}
              </li>
            ))}
          </ul>

          <button
            className="md:hidden p-3 text-white/90 hover:text-white transition-colors"
            onClick={toggleMobileMenu}
            aria-label="切换菜单"
            aria-expanded={isMobileMenuOpen}
          >
            <i className={`fas text-lg ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-white/15 animate-slide-up">
            <ul className="py-2">
              {NAV_ITEMS.map((item) => (
                <li key={item.path}>
                  {item.children ? (
                    <div>
                      <button
                        onClick={() => toggleDropdown(item.path)}
                        className={`w-full flex items-center justify-between px-1 py-3 text-sm transition-colors ${
                          isDropdownActive(item)
                            ? 'text-white font-medium'
                            : 'text-white/85'
                        }`}
                        aria-expanded={openDropdown === item.path}
                      >
                        <span>{item.label}</span>
                        <i
                          className={`fas fa-chevron-down text-[10px] transition-transform duration-200 ${
                            openDropdown === item.path ? 'rotate-180' : ''
                          }`}
                        ></i>
                      </button>

                      {openDropdown === item.path && (
                        <ul className="pb-2 pl-3 border-l border-white/20 ml-1">
                          {item.children.map((child) => (
                            <li key={child.path}>
                              <NavLink
                                to={child.path}
                                className={`block py-2 text-sm transition-colors ${
                                  isActive(child.path)
                                    ? 'text-white font-medium'
                                    : 'text-white/70 hover:text-white'
                                }`}
                                onClick={() => {
                                  setIsMobileMenuOpen(false);
                                  setOpenDropdown(null);
                                }}
                              >
                                {child.label}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <NavLink
                      to={item.path}
                      className={`block px-1 py-3 text-sm transition-colors ${
                        isActive(item.path)
                          ? 'text-white font-medium'
                          : 'text-white/85'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
